import range from 'lodash/range';

import {SectionItem} from 'common/components/items-menu/types';
import {getAlphaLabelFromIndex} from 'common/util';
import {RowLabelType} from 'common/query-builder/query-entry-section/types';
import {StepSelection} from 'common/report/funnels/models/types';

import {RangeSelection} from './types';

export function generateStepRanges({
  entry,
  steps,
  rowLabelType,
  disableAllSteps,
  disableEnteringFunnel,
  isReportConsistency,
  onlyAllSteps,
}: {
  entry: StepSelection;
  steps: number;
  rowLabelType?: RowLabelType;
  disableAllSteps?: boolean;
  disableEnteringFunnel?: boolean;
  isReportConsistency?: boolean;
  onlyAllSteps?: boolean;
}): Array<SectionItem & {value: RangeSelection}> {
  let hasMatch = false;
  const ranges = steps ? range(isReportConsistency ? 0 : 1, steps) : [];
  const {from: eventFrom, to: eventTo} = entry.range;

  const allEventsItem: SectionItem & {value: RangeSelection} = {
    isSelected: entry.isEntireRange,
    label: `All Steps`,
    tooltip: isReportConsistency
      ? {
          text: `Measure against completing the whole funnel.`,
        }
      : null,
    value: {isEntireRange: true, range: {}},
  };

  if (disableAllSteps) {
    allEventsItem.isDisabled = true;
    allEventsItem.tooltip = {text: `All steps is not supported for this chart.`};
  }

  const rangeItems = ranges.map((i) => {
    const _range: RangeSelection['range'] = {from: i, to: i + 1};
    const isSelected = eventFrom === _range.from && eventTo === _range.to;
    hasMatch = hasMatch || isSelected;

    const from = rowLabelType === RowLabelType.Alpha ? getAlphaLabelFromIndex(_range.from - 1) : _range.from;
    const to = rowLabelType === RowLabelType.Alpha ? getAlphaLabelFromIndex(_range.to - 1) : _range.to;
    const label = isReportConsistency && from === 0 ? `Entering Funnel` : `Step ${from} \u2192 ${to}`;

    let tooltip =
      from === 0
        ? `Measure against entering the funnel. Typically only applies to measuring uniques, totals or sessions.`
        : `Measure against a step range. When measuring uniques, totals or sessions, counts instances of reaching step ${to}.`;

    let isDisabled = onlyAllSteps;
    if (onlyAllSteps) {
      tooltip = `Step selection disabled in Funnel Steps visualization.`;
    }
    if (disableEnteringFunnel && from === 0) {
      isDisabled = true;
      tooltip = `Entering Funnel is disabled with conversion rate and time to convert measurements.`;
    }

    return {
      isSelected,
      isDisabled,
      label,
      tooltip: isReportConsistency ? {text: tooltip, 'max-width': `200px`} : null,
      value: {isEntireRange: false, range: _range},
    };
  });

  // if event has no match, it means a multi step
  // range was previously selected
  if (!entry.isEntireRange && !hasMatch) {
    rangeItems.push({
      isSelected: true,
      isDisabled: false,
      label: `Step ${eventFrom} \u2192 Step ${eventTo}`,
      tooltip: null,
      value: {isEntireRange: false, range: entry.range},
    });
  }

  return [allEventsItem, ...rangeItems];
}
